import { fetchAllAdminUsers } from '@actions/adminUsers';
import {
    fetchChallengeGroupOptionsForCalendar,
    fetchChallengesForCalendar,
    setCalendarFilterData,
} from '@actions/calendar';
import {
    getCalendarChallenges,
    getCalendarIsFetching,
    getCalendarFetchError,
    getMyCalendarChallengeGroupOptions,
    getAllCalendarChallengeGroupOptions,
    getCalendarFilterData,
} from '@selectors/calendar';
import usePrevious from 'lib/src/hooks/usePrevious';
import { clearFieldErrors } from 'lib/src/redux/actions/fieldErrors';
// @ts-ignore todo change to TS
import { getFieldError } from 'lib/src/redux/selectors/fieldErrors';
import { useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
    CalendarChallengeGroupDropdownOption,
    CalendarChallengeGroupOption,
} from 'src/types/shared/Challenge';

const getDropDownOption = (item: CalendarChallengeGroupOption) => ({
    value: item.challengeGroupID,
    label: item.challengeGroupName,
});

const getValue = (option: CalendarChallengeGroupDropdownOption) => option.value;

const extraDropdownOptions: CalendarChallengeGroupDropdownOption[] = [
    { value: -1, label: 'All Groups' },
    { value: 0, label: 'My Groups' },
];

const useFetchCalendarChallenges = () => {
    const dispatch = useDispatch();

    const calendarChallenges = useSelector(getCalendarChallenges);
    const isFetching = useSelector(getCalendarIsFetching);
    const fetchError = useSelector(getCalendarFetchError);
    const startDateError = useSelector(state => getFieldError(state, 'FilterFrom'));
    const endDateError = useSelector(state => getFieldError(state, 'FilterTo'));
    const challengeGroupIDsError = useSelector(state => getFieldError(state, 'ChallengeGroupIDs'));
    const myChallengeGroups = useSelector(getMyCalendarChallengeGroupOptions).map(
        getDropDownOption,
    );
    const allChallengeGroups = useSelector(getAllCalendarChallengeGroupOptions)
        .map(getDropDownOption)
        .sort((a, b) => a.label.localeCompare(b.label));
    const { startDate, endDate, challengeGroupIDs } = useSelector(getCalendarFilterData);

    const [selectedChallengeID, setSetelectedChallengeID] = useState<number | null>(null);

    const prevProps = usePrevious({ challengeGroupIDs, startDate, endDate });

    useEffect(() => {
        dispatch(clearFieldErrors());
        batch(() => {
            dispatch(
                fetchChallengesForCalendar(
                    challengeGroupIDs.filter(id => id > 0),
                    startDate.toISOString(),
                    endDate.toISOString(),
                ),
            );
            dispatch(fetchChallengeGroupOptionsForCalendar());
            dispatch(fetchAllAdminUsers());
        });
    }, []); // Fetch on page load

    useEffect(() => {
        let ids = [...challengeGroupIDs];
        if (challengeGroupIDs !== prevProps.challengeGroupIDs) {
            if (challengeGroupIDs.includes(-1)) ids = [];
            else if (challengeGroupIDs.includes(0)) ids = myChallengeGroups.map(getValue);
        }
        if (
            challengeGroupIDs !== prevProps.challengeGroupIDs ||
            startDate !== prevProps.startDate ||
            endDate !== prevProps.endDate
        ) {
            dispatch(clearFieldErrors());
            dispatch(
                fetchChallengesForCalendar(
                    ids.filter(id => id > 0),
                    startDate.toISOString(),
                    endDate.toISOString(),
                ),
            );
        }
    }, [challengeGroupIDs, startDate, endDate, prevProps, allChallengeGroups, myChallengeGroups]); // Re-fetch on state changes

    const setChallengeGroupIDs = (ids: number[]) =>
        dispatch(setCalendarFilterData({ challengeGroupIDs: ids }));
    const setStartDate = (date: Date) => dispatch(setCalendarFilterData({ startDate: date }));
    const setEndDate = (date: Date) => dispatch(setCalendarFilterData({ endDate: date }));

    return {
        calendarChallenges,
        challengeGroupIDs,
        setChallengeGroupIDs,
        isFetching,
        fetchError,
        fieldErrors: { startDateError, endDateError, challengeGroupIDsError },
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        challengeGroupOptions: [...extraDropdownOptions, ...allChallengeGroups],
        selectedChallengeID,
        setSetelectedChallengeID,
    };
};

export default useFetchCalendarChallenges;
